// @flow
import { append } from "ramda"

import type { SearchFormState, SearchMode } from "common/records/SearchFormState.types"
import type Options from "common/records/Options"
import { PLACE_VALUE_MODES } from "../../consts/place"
import FiltersState from "../../records/FiltersState"

/**
 * Checks, if filter is applicable, if not wont be used in returned query
 * @params needed params for FiltersState.isApplicable function
 * returns function which takes filterName and checks if is applicable
 */
const getIsApplicable = (
  mode: SearchMode,
  largePlace: boolean,
  anywhere: boolean,
  hiddenFeatures: boolean,
  useAggregateResults: boolean,
  isLastDestinationEmpty: boolean,
) => (filterName: string) =>
  FiltersState.isApplicable(
    filterName,
    mode,
    largePlace,
    anywhere,
    hiddenFeatures,
    useAggregateResults,
    isLastDestinationEmpty,
  )

export default function filtersToQuery(
  filtersState: FiltersState,
  searchForm?: SearchFormState,
  options?: Options,
): {} {
  const query = {}

  const isApplicable =
    searchForm && options
      ? getIsApplicable(
          searchForm.getMode(),
          searchForm.get("destination").isLarge(),
          searchForm.get("destination").getMode() === PLACE_VALUE_MODES.ANYWHERE,
          options.get("enableHiddenFeatures"),
          searchForm.useAggregateResults(options),
          searchForm.isLastDestinationEmpty(),
        )
      : // eslint-disable-next-line no-unused-vars
        (_: string) => true

  if (filtersState.isSet("price") && isApplicable("price")) {
    query.priceMin = filtersState.price.min
    query.priceMax = filtersState.price.max
  }

  // query.dayTimes = "departure-1-16|arrival-0-17"
  if (filtersState.isSet("dayTimes") && isApplicable("dayTimes")) {
    query.dayTimes = filtersState
      .get("dayTimes")
      .dayTimesList.map((dayTimesFilter, index) => {
        if (!dayTimesFilter || filtersState.dayTimes.isDefault(index)) {
          return "x"
        }
        return `${dayTimesFilter.type}-${dayTimesFilter.min}-${dayTimesFilter.max}`
      })
      .join("_")
  }

  if (filtersState.isSet("daysInWeek") && isApplicable("daysInWeek")) {
    query.daysInWeek = filtersState
      .get("daysInWeek")
      .daysInWeekList.map((daysInWeekFilter, index) => {
        if (!daysInWeekFilter || filtersState.daysInWeek.isDefault(index)) {
          return "x"
        }
        return daysInWeekFilter
          .reduce((acc, val, key) => (val ? append(key, acc) : acc), [])
          .join("")
      })
      .join("-")
  }

  if (filtersState.isSet("flightDuration") && isApplicable("flightDuration")) {
    query.flightDurationMax = filtersState.flightDuration
  }

  // Set stop duration max and min separately
  if (filtersState.isSet("stopDuration") && isApplicable("stopDuration")) {
    if (filtersState.isSet("stopDurationMin")) {
      query.stopDurationMin = filtersState.stopDuration.min
    }
    if (filtersState.isSet("stopDurationMax")) {
      query.stopDurationMax = filtersState.stopDuration.max
    }
  }

  if (filtersState.isSet("directFlights")) {
    query.directFlights = filtersState.directFlights ? "true" : "false"
  }

  if (filtersState.isSet("stopNumber") && isApplicable("stopNumber")) {
    query.stopNumber = filtersState.stopNumber
  }

  if (
    filtersState.isSet("returnFromDifferentAirport") &&
    isApplicable("returnFromDifferentAirport")
  ) {
    query.returnFromDifferentAirport = filtersState.returnFromDifferentAirport ? "true" : "false"
  }

  if (filtersState.isSet("returnToDifferentAirport") && isApplicable("returnToDifferentAirport")) {
    query.returnToDifferentAirport = filtersState.returnToDifferentAirport ? "true" : "false"
  }

  if (filtersState.isSet("noBags") && isApplicable("noBags")) {
    query.noBags = filtersState.noBags ? "true" : "false"
  }

  if (filtersState.isSet("stopoverAirports") && isApplicable("airports")) {
    query.airportsList = filtersState.stopoverAirports.selected.join(",")
    query.selectedStopoverAirportsExclude = (filtersState.stopoverAirports
      .excludeSelectedItems: boolean)
      ? "true"
      : "false"
  }

  if (filtersState.isSet("airlines") && isApplicable("airlines")) {
    query.airlinesList = filtersState.airlines.selected.join(",")
    query.selectedAirlinesExclude = (filtersState.airlines.excludeSelectedItems: boolean)
      ? "true"
      : "false"
  }

  // TODO: check if these filters should be reset
  if (filtersState.isSet("sortBy")) {
    query.sortBy = filtersState.sortBy
  }

  if (filtersState.isSet("sortAggregateBy")) {
    query.sortAggregateBy = filtersState.sortAggregateBy
  }

  if (filtersState.isSet("locationHashtags")) {
    query.locationHashtags = filtersState.locationHashtags.selected.join(",")
  }

  if (filtersState.isSet("transport")) {
    query.transport = filtersState.transport.selected.join(",")
  }

  return query
}
